import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useAuth } from '../AuthComponent';
import { ChevronRightIcon, ExternalIcon, RefreshIcon, SearchIcon, XSmallIcon } from '@shopify/polaris-icons';
import { Ic, StatusPill, EmDash } from '../ui';
import { reviewApi } from './apiClient';

// name, what it checks, and how the number is worked out (from the B2B Order Review rules file)
const RULES = {
  1: ['Footwear: category cover', 'Adult and Youth FG, Indoor and Turf. Flags above 30 weeks of cover.',
    'blended = 40% x (last 4 weeks / 4) + 60% x (same 2 months last year / 8). Cover = (on hand + on order + this order) / blended.'],
  2: ['Adult FG: price tier cover', 'Elite $200+, Pro $120-199, Academy / League $80-120, Club under $80. Flags above 25 weeks.',
    'Same blended rate and cover, grouped by price tier.'],
  3: ['Adult FG: style cover', 'Superfly, Vapor, Tiempo, Phantom, Predator, F50, Copa. Flags above 25 weeks.',
    'Same blended rate and cover, grouped by style.'],
  4: ['Footwear: size mix', 'Flags a size that would hold more than its benchmark share of the category.',
    'Flags when the projected share is above benchmark x tier multiplier and the projected units are at least that share of the category plus 4. Skipped when the category has under 50 units or the size has 2 or fewer.'],
  5: ['Jerseys: recent sales', 'Per SKU: what the store has sold since it first received the jersey. Flags above 60 days of cover.',
    'Velocity = units sold / days on hand (first receipt to today, or to the last sale if sold out). Cover = (on hand + on order + this order) / velocity.'],
  6: ['Warehouse remaining', 'Current-season footwear and jerseys. Flags a bottom-third store when the warehouse would keep under 5 available of a SKU. MLS team jerseys are excluded.',
    'Stores are ranked by the last 30 days of sales and split into three equal groups. Tier 3 is the lowest. Warehouse stock is the Shopify available quantity at the Tinton Falls Warehouse, which already excludes this order.'],
  7: ['Duplicate orders', 'Flags any SKU already on order or in transit to this store in Lightspeed.', 'Lightspeed on-order quantity and transfers sent to the store.'],
  8: ['Line count', 'Flags more than 40 distinct SKU lines.', 'Lines = distinct SKUs on the order.'],
  9: ['Ordering by category', 'Flags an order that spans more than one product group, unless every item is a Soccer Post product.',
    'Groups: Adult FG / TF / IN, Youth FG / TF / IN, Bags, Balls, GK gloves and apparel, Replica jerseys and apparel, Shin guards, Socks, Training apparel.'],
  10: ['Order quantity limits', 'Footwear over 2 per SKU, jersey size limits, and limits for items sold in packs (socks, cones, merchandise bags, the SPFC ball, vests).', 'Ordered quantity per line against its limit.'],
};
const RULE_IDS = Object.keys(RULES).map(Number);
const NUM = /^[-$\d.,%\s]+(wks|units|days)?$/;
const numericColumn = (rows, i) => rows.length > 0 && rows.every((r) => NUM.test(String(r[i])) || String(r[i]) === '-');

const shopifyOrderUrl = (id) => `https://admin.shopify.com/store/soccerpostb2b/orders/${id}`;

const FILTERS = [['attention', 'Needs review'], ['held', 'On hold'], ['all', 'All'], ['passed', 'Passed'], ['reviewed', 'Reviewed']];

function when(iso) {
  if (!iso) return '';
  const d = new Date(iso);
  const today = new Date();
  const yest = new Date(today.getTime() - 86400000);
  const time = d.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' });
  if (d.toDateString() === today.toDateString()) return `Today ${time}`;
  if (d.toDateString() === yest.toDateString()) return `Yesterday ${time}`;
  return `${d.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })} ${time}`;
}

function ResultBadge({ order }) {
  if (order.hold?.status === 'held') return <StatusPill tone="hold">On hold</StatusPill>;
  if (order.cancelled) return <StatusPill tone="can">Cancelled</StatusPill>;
  if (order.reviewed_at) return <StatusPill tone="neu">Reviewed</StatusPill>;
  if (order.status === 'flag') return <StatusPill tone="can">{order.flags.length} flag{order.flags.length === 1 ? '' : 's'}</StatusPill>;
  if (order.status === 'nomatch') return <StatusPill tone="hold">No store match</StatusPill>;
  if (order.status === 'error') return <StatusPill tone="can">Could not review</StatusPill>;
  return <StatusPill tone="ful">Passed</StatusPill>;
}

function RuleRow({ rule, open, onToggle }) {
  const [name, blurb, formula] = RULES[rule.id];
  const flag = rule.status === 'flag';
  const pill = flag ? <StatusPill tone="can">Flagged</StatusPill>
    : rule.status === 'skip' ? <StatusPill tone="neu">Skipped</StatusPill> : <StatusPill tone="ful">Passed</StatusPill>;
  return (
    <div className="uh-br-rule">
      <button type="button" className={`uh-br-rule-h${flag ? ' f' : ''}`} aria-expanded={open} onClick={onToggle}>
        <span className="num">{rule.id}</span>
        <span className="txt"><span className="t">{name}</span><span className="why">{rule.why}</span></span>
        {pill}
        <span className="chev"><Ic source={ChevronRightIcon} sm /></span>
      </button>
      {open && (
        <div className="uh-br-rule-b">
          <div className="uh-br-formula">{blurb}<br />{formula}</div>
          {rule.rows.length > 0 && (
            <div className="uh-tscroll uh-br-mini">
              <table className="uh-tbl">
                <thead><tr>{rule.headers.map((h, i) => <th key={h} className={numericColumn(rule.rows, i) ? 'r' : ''}>{h}</th>)}</tr></thead>
                <tbody>
                  {rule.rows.map((r, ri) => (
                    <tr key={ri} className={rule.hits.includes(ri) ? 'hit' : ''}>
                      {r.map((v, i) => <td key={i} className={numericColumn(rule.rows, i) ? 'r uh-num' : ''}>{String(v)}</td>)}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

function OrderDialog({ dlgRef, orderId, ids, onOpen, onChanged, onClose }) {
  const { nameOf } = useAuth();
  const [order, setOrder] = useState(null);
  const [open, setOpen] = useState({});
  const [busy, setBusy] = useState('');
  const [error, setError] = useState('');

  useEffect(() => {
    if (!orderId) return undefined;
    let live = true;
    setOrder(null); setError('');
    reviewApi.order(orderId).then((o) => {
      if (!live) return;
      setOrder(o);
      const first = {};
      (o.result.rules || []).filter((r) => r.status === 'flag').forEach((r) => { first[r.id] = true; });
      setOpen(first);
    }, (e) => live && setError(e.message));
    return () => { live = false; };
  }, [orderId]);

  const [confirmCancel, setConfirmCancel] = useState(false);
  useEffect(() => { setConfirmCancel(false); }, [orderId]);
  const act = async (kind) => {
    setBusy(kind); setError('');
    try {
      const call = { rerun: reviewApi.rerun, review: reviewApi.markReviewed, release: reviewApi.releaseHold, cancel: reviewApi.cancelOrder }[kind];
      const o = await call(orderId);
      if (kind === 'rerun') setOrder(o); else setOrder((prev) => ({ ...prev, ...o }));
      setConfirmCancel(false);
      onChanged();
    } catch (e) { setError(e.message); }
    setBusy('');
  };

  const at = ids.indexOf(orderId);
  const result = order?.result || {};
  const rules = result.rules || [];
  const needsReview = order && !order.reviewed_at && order.status !== 'pass';

  return (
    <dialog className="uh-dlg uh-br-dlg" ref={dlgRef} aria-labelledby="br-dlg-h" onClose={onClose}
      onClick={(e) => { if (e.target === dlgRef.current) dlgRef.current.close(); }}>
      <div className="uh-dlg-h">
        <h2 id="br-dlg-h" className="uh-mono">{order?.order_name || 'Order'}</h2>
        {order && (
          <a className="uh-ibtn" href={shopifyOrderUrl(order.order_id)} target="_blank" rel="noopener noreferrer"
            aria-label={`Open ${order.order_name} in Shopify`} title="Open in Shopify"><Ic source={ExternalIcon} sm /></a>
        )}
        <span className="uh-sp" />
        {order && <ResultBadge order={order} />}
        <button type="button" className="uh-ibtn" aria-label="Close" onClick={() => dlgRef.current.close()}><Ic source={XSmallIcon} sm /></button>
      </div>
      <div className="uh-br-scroll">
        {error && <div className="uh-alert" role="alert"><div>{error}</div></div>}
        {!order && !error && <div className="uh-empty">Loading…</div>}
        {order && (
          <>
            {order.hold && (
              <div className={`uh-br-hold${order.hold.status === 'held' ? ' on' : ''}`} role="status">
                {order.hold.status === 'held' ? (
                  <>
                    <div><b>On hold.</b> This order ships to {order.shop_name || order.ship_to}, a store whose orders wait for a person. It is on hold in Shopify, so it is not picked and no purchase order is created until you release it.
                      {order.hold.po_created && <> <b>A Lightspeed purchase order already exists for it.</b> If you cancel the order, cancel that purchase order in Lightspeed too.</>}</div>
                    {confirmCancel ? (
                      <div className="uh-br-hold-actions">
                        <span>Cancel {order.order_name}? Shopify cancels it and puts the stock back. There is no refund and no email to the customer.</span>
                        <button type="button" className="uh-btn slim" disabled={!!busy} onClick={() => setConfirmCancel(false)}>Keep it</button>
                        <button type="button" className={`uh-btn slim danger${busy === 'cancel' ? ' busy' : ''}`} disabled={!!busy} onClick={() => act('cancel')}>{busy === 'cancel' ? 'Cancelling…' : 'Yes, cancel the order'}</button>
                      </div>
                    ) : (
                      <div className="uh-br-hold-actions">
                        <button type="button" className={`uh-btn slim primary${busy === 'release' ? ' busy' : ''}`} disabled={!!busy} onClick={() => act('release')}>{busy === 'release' ? 'Releasing…' : 'Release hold'}</button>
                        <button type="button" className="uh-btn slim danger" disabled={!!busy} onClick={() => setConfirmCancel(true)}>Cancel order…</button>
                      </div>
                    )}
                  </>
                ) : (
                  <div>{order.hold.resolved_by === 'Shopify'
                    ? <>Hold ended in Shopify ({order.hold.status === 'cancelled' ? 'the order was cancelled there' : 'the order was released or shipped there'}) {when(order.hold.resolved_at)}.</>
                    : <>Hold {order.hold.status === 'released' ? 'released' : 'ended by cancelling the order'} by {nameOf(order.hold.resolved_by)} {when(order.hold.resolved_at)}.</>}</div>
                )}
              </div>
            )}
            <div className="uh-br-top">
              <div className="uh-br-meta">
                <div><div className="k">Placed</div>{when(order.placed_at)}</div>
                <div><div className="k">Lines / units</div><span className="uh-num">{order.line_count} / {order.unit_count}</span></div>
                <div><div className="k">Shopify tag</div>{order.tagged ? <span className="uh-br-tag">Review Flagged</span> : <EmDash />}</div>
              </div>
              {order.shop_id
                ? <div className="uh-br-match">Ships to <b>{order.ship_to}</b>, matched to Lightspeed shop <b>{order.shop_name}</b>.
                  {result.stock_as_of && <span className="uh-muted"> Stock counts as of {when(result.stock_as_of)}.</span>}</div>
                : order.status === 'excluded'
                  ? <div className="uh-br-match">Ships to <b>{order.ship_to}</b>.</div>
                  : <div className="uh-br-match bad"><b>{result.message || `No Lightspeed shop matches “${order.ship_to}”.`}</b> Add the shop match in Lightspeed, then Re-run.</div>}
              {order.status === 'excluded' && <div className="uh-br-match warn">Placed by {order.customer || 'a customer'} whose orders are not reviewed, so no rules were run on it.</div>}
              {result.unknown_skus?.length > 0 && (
                <div className="uh-br-match warn">Not found in Lightspeed, so no rule could check them: <span className="uh-mono">{result.unknown_skus.join(', ')}</span></div>
              )}
              {order.status === 'error' && <div className="uh-br-match bad">{result.message}</div>}
              {order.reviewed_at && <div className="uh-muted">Marked reviewed by {nameOf(order.reviewed_by)} {when(order.reviewed_at)}.</div>}
            </div>
            {rules.length > 0 && (
              <div className="uh-br-rules">
                {RULE_IDS.map((id) => {
                  const rule = rules.find((r) => r.id === id);
                  return rule ? <RuleRow key={id} rule={rule} open={!!open[id]} onToggle={() => setOpen((o) => ({ ...o, [id]: !o[id] }))} /> : null;
                })}
              </div>
            )}
          </>
        )}
      </div>
      <div className="uh-br-foot">
        <button type="button" className="uh-btn slim" disabled={at <= 0} onClick={() => onOpen(ids[at - 1])}>← Previous order</button>
        <button type="button" className="uh-btn slim" disabled={at < 0 || at >= ids.length - 1} onClick={() => onOpen(ids[at + 1])}>Next order →</button>
        <span className="uh-sp" />
        <button type="button" className={`uh-btn slim${busy === 'rerun' ? ' busy' : ''}`} disabled={!order || !!busy} onClick={() => act('rerun')}>
          <Ic source={RefreshIcon} sm />{busy === 'rerun' ? 'Reviewing…' : 'Re-run'}
        </button>
        {needsReview && (
          <button type="button" className={`uh-btn slim primary${busy === 'review' ? ' busy' : ''}`} disabled={!!busy} onClick={() => act('review')}>
            {busy === 'review' ? 'Saving…' : 'Mark reviewed'}
          </button>
        )}
      </div>
    </dialog>
  );
}

export default function B2BOrderReview() {
  const [filter, setFilter] = useState('attention');
  const [store, setStore] = useState('');
  const [days, setDays] = useState(7);
  const [q, setQ] = useState('');
  const [query, setQuery] = useState('');
  const [page, setPage] = useState(1);
  const [data, setData] = useState({ orders: [], total: 0, page_size: 50 });
  const [info, setInfo] = useState(null);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(true);
  const [openId, setOpenId] = useState(null);
  const dlgRef = useRef(null);

  useEffect(() => { const t = setTimeout(() => { setQuery(q); setPage(1); }, 300); return () => clearTimeout(t); }, [q]);

  const load = useCallback(async () => {
    try {
      const [orders, status] = await Promise.all([reviewApi.orders({ status: filter, q: query, store, days, page }), reviewApi.status()]);
      setData(orders); setInfo(status); setError('');
    } catch (e) { setError(e.message); }
    setLoading(false);
  }, [filter, query, store, days, page]);
  useEffect(() => { setLoading(true); load(); }, [load]);

  const open = (id) => {
    setOpenId(id);
    if (dlgRef.current && !dlgRef.current.open) dlgRef.current.showModal();
  };

  const stats = info?.stats;
  const pages = Math.max(1, Math.ceil(data.total / data.page_size));
  const history = info?.history;

  return (
    <section className="uh-page">
      <div className="uh-ph">
        <div>
          <h1 className="uh-h1">B2B order review</h1>
          <div className="uh-sub">
            SP Corp orders checked against Lightspeed sales and stock for the store they ship to.
            {stats?.last_checked && <> Last check {when(stats.last_checked)}.</>}
          </div>
        </div>
        {stats && (
          <div className="uh-br-stats">
            {[[stats.checked, 'Checked (7 days)'], [stats.need_review, 'Need review'], [stats.held, 'On hold'], [stats.passed, 'Passed'], [stats.reviewed, 'Reviewed']].map(([v, k]) => (
              <div className="stat" key={k}><span className="v uh-num">{v}</span><span className="k">{k}</span></div>
            ))}
          </div>
        )}
      </div>

      {error && <div className="uh-alert" role="alert"><div>{error}</div></div>}
      {history && !history.ready && (
        <div className="uh-alert" role="status"><div><b>Sales history is still loading.</b> Orders are not reviewed until the last year of Lightspeed sales has been loaded ({history.shops_loaded} locations so far).</div></div>
      )}
      {info && !info.can_write_shopify && (
        <div className="uh-alert" role="status"><div>This is a test copy. Orders are reviewed here, but nothing is tagged in Shopify.</div></div>
      )}

      <div className="uh-card">
        <div className="uh-br-tbar">
          <div className="uh-br-seg" role="group" aria-label="Filter orders">
            {FILTERS.map(([id, label]) => (
              <button key={id} type="button" aria-pressed={filter === id} onClick={() => { setFilter(id); setPage(1); }}>{label}</button>
            ))}
          </div>
          <select className="uh-select" aria-label="Store" value={store} onChange={(e) => { setStore(e.target.value); setPage(1); }}>
            <option value="">All stores</option>
            {(info?.stores || []).map((s) => <option key={s} value={s}>{s}</option>)}
          </select>
          <select className="uh-select" aria-label="Date range" value={days} onChange={(e) => { setDays(Number(e.target.value)); setPage(1); }}>
            <option value={7}>Last 7 days</option><option value={30}>Last 30 days</option><option value={90}>Last 90 days</option>
          </select>
          <div className="uh-search">
            <Ic source={SearchIcon} sm />
            <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Order # or store" aria-label="Search orders" />
          </div>
          <span className="uh-sp" />
          <span className="uh-muted">{data.total} order{data.total === 1 ? '' : 's'}</span>
        </div>
        <div className="uh-tscroll">
          <table className="uh-tbl">
            <thead><tr><th>Order</th><th>Placed</th><th>Customer</th><th>Ships to</th><th className="r">Lines</th><th className="r">Units</th><th>Result</th><th>Rules flagged</th><th /></tr></thead>
            <tbody>
              {data.orders.map((o) => (
                <tr key={o.order_id} className="click" onClick={() => open(o.order_id)}>
                  <td className="nm uh-mono">{o.order_name}</td>
                  <td>{when(o.placed_at)}</td>
                  <td>{o.customer || <EmDash />}</td>
                  <td>
                    <span className="uh-br-route"><span className="nm">{o.ship_to.replace(/^Soccer Post /, '')}</span>
                      {o.shop_name && <span className="loc">LS: {o.shop_name.replace(/^Soccer Post /, '')}</span>}</span>
                  </td>
                  <td className="r uh-num">{o.line_count}</td>
                  <td className="r uh-num">{o.unit_count}</td>
                  <td><ResultBadge order={o} /></td>
                  <td>{o.status === 'flag' && o.flags.length ? <span className="uh-br-flags">{o.flags.map((n) => <i key={n} title={RULES[n]?.[0]}>{n}</i>)}</span> : <EmDash />}</td>
                  <td className="go"><Ic source={ChevronRightIcon} sm /></td>
                </tr>
              ))}
              {!loading && data.orders.length === 0 && (
                <tr><td colSpan={8}><div className="uh-empty">{filter === 'attention' ? 'No orders need review.' : filter === 'held' ? 'No orders are on hold.' : 'No orders here.'}</div></td></tr>
              )}
              {loading && data.orders.length === 0 && <tr><td colSpan={8}><div className="uh-empty">Loading…</div></td></tr>}
            </tbody>
          </table>
        </div>
        <div className="uh-br-pager">
          <button type="button" className="uh-btn slim" disabled={page <= 1} onClick={() => setPage(page - 1)}>← Previous</button>
          <span>Page {page} of {pages}</span>
          <button type="button" className="uh-btn slim" disabled={page >= pages} onClick={() => setPage(page + 1)}>Next →</button>
        </div>
        <div className="uh-tfoot">
          <span>New orders are checked about every 15 minutes. Orders for the Tinton Falls Warehouse and “… Teams” locations are not checked. Orders from the last 7 days that are not yet fulfilled are included.</span>
        </div>
      </div>

      <OrderDialog dlgRef={dlgRef} orderId={openId} ids={data.orders.map((o) => o.order_id)} onOpen={open} onChanged={load} onClose={() => setOpenId(null)} />
    </section>
  );
}
